import { moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  FeedLoadAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import type { NestedThreadLoadFulfilledAction } from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type { SuggestedActionsLoadFulfilledAction } from '../../domains/suggestedActions/actions.types';
import type { SuggestedLeadersLoadAction } from '../../domains/suggestedLeaders/loadActions.types';
import type { SuggestedLeadersLoadMoreAction } from '../../domains/suggestedLeaders/loadMoreActions.types';
import type { SuggestedUsersLoadAction } from '../../domains/suggestedUsers/loadActions.types';
import type { SuggestedUsersLoadMoreAction } from '../../domains/suggestedUsers/loadMoreActions.types';
import type { UserFollowAction } from '../../domains/userFollow/followActions.types';
import type { UserUnfollowAction } from '../../domains/userFollow/unfollowActions.types';
import type { UserFollowedUsersLoadAction } from '../../domains/userFollowedUsers/actions.types';
import type { UserHeaderLoadAction } from '../../domains/userHeaderLoad/actions.types';
import type { ViewerIsFollowingUserLoadFulfilledAction } from '../../domains/viewerIsFollowingUsers/actions.types';
import type { UserFollowedByLoadAction } from '../userFollowedBy/reducer';

import type { ViewerIsFollowingUsers } from './types';
export type ViewerIsFollowingUsersAction =
  | FeedLoadAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | UserHeaderLoadAction
  | UserFollowAction
  | UserUnfollowAction
  | UserFollowedUsersLoadAction
  | UserFollowedByLoadAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | SuggestedActionsLoadFulfilledAction
  | SuggestedLeadersLoadAction
  | SuggestedLeadersLoadMoreAction
  | SuggestedUsersLoadAction
  | SuggestedUsersLoadMoreAction
  | ViewerIsFollowingUserLoadFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction;

const initialState: ViewerIsFollowingUsers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<ViewerIsFollowingUsers, ViewerIsFollowingUsersAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_FOLLOWED_BY.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_FOLLOWED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_ACTION.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_LEADERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_LEADERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_USERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'VIEWER_IS_FOLLOWING_USER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER.FOLLOW_FULFILLED':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_FULFILLED':
      return update(state, action.payload.id, { isFollowing: false });
    case 'USER.FOLLOW_PENDING':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_REJECTED':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_PENDING':
      return update(state, action.payload.id, { isFollowing: false });
    case 'USER.FOLLOW_REJECTED':
      return update(state, action.payload.id, { isFollowing: false });
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    default:
      return state;
  }
};

export default reducer;
